import clsx from "clsx";
import type { ComponentPropsWithoutRef } from "react";
import { Link } from "react-router-dom";

type PrimaryButtonProps = {
  children: React.ReactNode;
  className?: string;
  size?: "default" | "large";
} & (
  | { to: string }
  | { to?: never; onClick?: ComponentPropsWithoutRef<"button">["onClick"]; type?: "button" | "submit" }
);

export function PrimaryButton({ children, className, size = "default", ...rest }: PrimaryButtonProps) {
  const base =
    "font-mono font-semibold text-primary-foreground bg-primary rounded-button transition-colors hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";
  const sizeClasses =
    size === "large"
      ? "rounded-button-lg px-8 py-4 text-[length:var(--font-size-headline-small)]"
      : "rounded-button px-4 py-2 text-[length:var(--font-size-title-medium)]";

  const classes = clsx(base, sizeClasses, className);

  if ("to" in rest && rest.to) {
    return (
      <Link to={rest.to} className={classes} data-testid="primary-button-link">
        {children}
      </Link>
    );
  }

  const buttonProps = rest as {
    onClick?: ComponentPropsWithoutRef<"button">["onClick"];
    type?: "button" | "submit";
  };
  const { onClick, type = "button" } = buttonProps;
  return (
    <button type={type} onClick={onClick} className={classes} data-testid="primary-button">
      {children}
    </button>
  );
}
